import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useOutletContext } from 'react-router-dom';
import { API, auth, inputCls, Field, Modal, PageHead, PrimaryBtn, Paginator, errMsg, money, EXPENSE_CATS, PAY_METHODS, StatusBadge, fmtDate } from './finance/shared';
import { canViewFinance } from '../../config/organization';
import { EmptyState } from '../../components/dashboard/TodaySchedule';
import Icon from '../../components/dashboard/DashboardIcons';
import { useLiveCountdown } from '../../lib/matchCountdown';

const STATUS = { upcoming: 'قادمة', live: 'جارية', awaiting_result: 'بانتظار النتيجة', completed: 'مكتملة', finished: 'منتهية', cancelled: 'ملغية' };
const STATUS_CLS = { upcoming: 'bg-blue-50 text-blue-700', live: 'bg-red-50 text-red-600', awaiting_result: 'bg-amber-50 text-amber-700', completed: 'bg-teal-50 text-teal-700', finished: 'bg-teal-50 text-teal-700', cancelled: 'bg-slate-100 text-slate-500' };
const isDone = (s) => s === 'completed' || s === 'finished';
const isTerminal = (s) => isDone(s) || s === 'cancelled';
const STATUS_RANK = { live: 0, awaiting_result: 1, upcoming: 2 };

const MatchCountdown = ({ match }) => {
  const text = useLiveCountdown(match.match_date, match.status);
  if (!text) return null;
  return <div className={`text-[11px] font-semibold ${match.status === 'awaiting_result' ? 'text-amber-700' : 'text-slate-400'}`} data-testid="match-countdown">{text}</div>;
};
const fmtDT = (d) => (d ? new Intl.DateTimeFormat('ar-SA-u-ca-gregory', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(d)) : '—');
const toLocalInput = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  return new Date(dt.getTime() - dt.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};

const TeamLogo = ({ url, fileId, name, className = 'h-8 w-8' }) => {
  const src = fileId ? `${API}/files/${fileId}` : url;
  return src ? <img src={src} alt="" className={`${className} rounded-lg bg-white object-contain`} onError={(e) => { e.target.style.display = 'none'; }} />
    : <div className={`flex ${className} items-center justify-center rounded-lg bg-[#16233b]/10 text-[10px] font-bold text-[#16233b]`}>{(name || '؟').slice(0, 2)}</div>;
};

const resultBadge = (m) => {
  if (!isDone(m.status) || m.home_score == null || m.away_score == null) return null;
  const clubHome = m.home_team_is_club, clubAway = m.away_team_is_club;
  if (clubHome === clubAway) return null;
  const clubScore = clubHome ? m.home_score : m.away_score;
  const otherScore = clubHome ? m.away_score : m.home_score;
  if (clubScore > otherScore) return <span className="rounded-full bg-teal-50 px-2 py-0.5 text-[10px] font-bold text-teal-700" data-testid="match-result-badge">فوز</span>;
  if (clubScore < otherScore) return <span className="rounded-full bg-red-50 px-2 py-0.5 text-[10px] font-bold text-red-600" data-testid="match-result-badge">خسارة</span>;
  return <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-bold text-amber-700" data-testid="match-result-badge">تعادل</span>;
};

export default function MatchesPage() {
  const { user, selectedSeasonId, seasons: ctxSeasons } = useOutletContext() || {};
  const seasons = Array.isArray(ctxSeasons) ? ctxSeasons : [];
  const defaultFormSeasonId = () => {
    if (selectedSeasonId && selectedSeasonId !== 'all' && selectedSeasonId !== 'unlinked') return selectedSeasonId;
    return seasons.find((s) => s.status === 'active')?.id || '';
  };
  const seasonQS = selectedSeasonId && selectedSeasonId !== 'all' ? `?season_id=${selectedSeasonId}` : '';
  const canFinance = canViewFinance(user);
  const [matches, setMatches] = useState([]);
  const [teams, setTeams] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [catFilter, setCatFilter] = useState('');
  const [tourFilter, setTourFilter] = useState('');
  const [page, setPage] = useState(1);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [formError, setFormError] = useState(null);
  const [detail, setDetail] = useState(null); // match being managed
  const [confirmDel, setConfirmDel] = useState(null);
  const [importState, setImportState] = useState(null);
  const fileRef = useRef(null);
  const [toast, setToast] = useState(null);
  const notify = (m) => { setToast(m); setTimeout(() => setToast(null), 3000); };

  const load = useCallback(async () => {
    setLoading(true);
    const g = (u) => axios.get(`${API}${u}`, auth()).then((r) => r.data).catch(() => []);
    const [m, t, c, tr] = await Promise.all([g(`/matches${seasonQS}`), g('/teams'), g('/categories'), g('/tournaments')]);
    const sorted = (Array.isArray(m) ? m : []).sort((a, b) => {
      const aTerm = isTerminal(a.status), bTerm = isTerminal(b.status);
      if (aTerm !== bTerm) return aTerm ? 1 : -1;
      if (aTerm) return new Date(b.match_date) - new Date(a.match_date);
      const ar = STATUS_RANK[a.status] ?? 2, br = STATUS_RANK[b.status] ?? 2;
      if (ar !== br) return ar - br;
      if (a.status === 'awaiting_result') return new Date(b.match_date) - new Date(a.match_date);
      return new Date(a.match_date) - new Date(b.match_date);
    });
    setMatches(sorted);
    setTeams(Array.isArray(t) ? t : []);
    setCategories(Array.isArray(c) ? c : []);
    setTournaments(Array.isArray(tr) ? tr : []);
    setLoading(false);
  }, [seasonQS]);
  useEffect(() => { load(); }, [load]);

  const openForm = (m) => {
    setEditing(m || null);
    setForm(m ? { ...m, match_date: toLocalInput(m.match_date), season_id: m.season_id || '' } : { home_team_id: '', away_team_id: '', category_id: '', tournament_id: '', match_type: 'external', match_date: '', venue: '', status: 'upcoming', notes: '', season_id: defaultFormSeasonId() });
    setFormError(null); setModal(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.home_team_id || !form.away_team_id || !form.category_id || !form.match_date) { setFormError('الفريقان والفئة وتاريخ المباراة حقول مطلوبة'); return; }
    if (form.home_team_id === form.away_team_id) { setFormError('لا يمكن أن يلعب الفريق ضد نفسه'); return; }
    const payload = {
      home_team_id: form.home_team_id, away_team_id: form.away_team_id, category_id: form.category_id,
      tournament_id: form.tournament_id || null, match_type: form.match_type || 'external',
      match_date: new Date(form.match_date).toISOString(), venue: form.venue?.trim() || null,
      status: form.status || 'upcoming', notes: form.notes?.trim() || null,
      season_id: form.season_id || null,
    };
    try {
      if (editing) await axios.put(`${API}/matches/${editing.id}`, payload, auth());
      else await axios.post(`${API}/matches`, payload, auth());
      setModal(false); notify('تم الحفظ بنجاح'); load();
    } catch (err) { setFormError(errMsg(err, 'فشل الحفظ')); }
  };

  const remove = async () => {
    try { await axios.delete(`${API}/matches/${confirmDel.id}`, auth()); notify('تم حذف المباراة وأحداثها'); setConfirmDel(null); load(); }
    catch (err) { notify(errMsg(err, 'تعذر الحذف')); setConfirmDel(null); }
  };

  const downloadTemplate = async () => {
    try {
      const res = await axios.get(`${API}/matches/excel-template/download`, { ...auth(), responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a'); a.href = url; a.download = 'matches_template.xlsx'; a.click(); URL.revokeObjectURL(url);
    } catch { notify('تعذر تنزيل النموذج'); }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setImportState({ busy: true });
    const fd = new FormData(); fd.append('file', file);
    try {
      const res = await axios.post(`${API}/matches/excel-import/preview`, fd, auth());
      setImportState({ preview: res.data });
    } catch (err) { setImportState({ error: errMsg(err, 'تعذر قراءة الملف') }); }
  };

  const confirmImport = async () => {
    setImportState((s) => ({ ...s, busy: true }));
    try {
      const res = await axios.post(`${API}/matches/excel-import/execute`, importState.preview, auth());
      setImportState(null); notify(res.data?.message || 'تم استيراد المباريات بنجاح'); load();
    } catch (err) { setImportState((s) => ({ ...s, busy: false, error: errMsg(err, 'فشل الاستيراد') })); }
  };

  const filtered = matches.filter((m) => {
    if (statusFilter === 'done' ? !isDone(m.status) : statusFilter && m.status !== statusFilter) return false;
    if (catFilter && m.category_id !== catFilter) return false;
    if (tourFilter && m.tournament_id !== tourFilter) return false;
    return true;
  });
  const paged = filtered.slice((page - 1) * 10, page * 10);
  const upcoming = matches.filter((m) => m.status === 'upcoming');
  const done = matches.filter((m) => isDone(m.status));
  const wins = done.filter((m) => {
    if (m.home_score == null || m.home_team_is_club === m.away_team_is_club) return false;
    return m.home_team_is_club ? m.home_score > m.away_score : m.away_score > m.home_score;
  });

  const selectCls = 'rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-sm text-slate-700 outline-none';

  return (
    <div className="mx-auto max-w-7xl space-y-4" data-testid="matches-page">
      {toast && <div className="fixed top-16 left-1/2 z-50 -translate-x-1/2 rounded-lg bg-[#16233b] px-4 py-2 text-sm text-white shadow-lg">{toast}</div>}
      <PageHead title="المباريات" desc="جدولة المباريات وإدارة النتائج والأهداف والبطاقات"
        action={
          <div className="flex flex-wrap gap-2">
            <button onClick={downloadTemplate} className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 hover:border-teal-300" data-testid="matches-template-btn"><Icon name="document" className="w-4 h-4" /> نموذج Excel</button>
            <button onClick={() => fileRef.current?.click()} className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 hover:border-teal-300" data-testid="matches-import-btn"><Icon name="arrowUp" className="w-4 h-4" /> استيراد</button>
            <input ref={fileRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={onFile} data-testid="matches-import-input" />
            <PrimaryBtn onClick={() => openForm(null)} data-testid="match-add-btn"><Icon name="plus" className="w-4 h-4" /> إضافة مباراة</PrimaryBtn>
          </div>
        } />

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <div className="rounded-xl border border-slate-200 bg-white p-3"><div className="text-xs text-slate-500">إجمالي المباريات</div><div className="text-xl font-bold text-slate-900" data-testid="matches-total">{matches.length}</div></div>
        <div className="rounded-xl border border-slate-200 bg-white p-3"><div className="text-xs text-slate-500">قادمة</div><div className="text-xl font-bold text-blue-700">{upcoming.length}</div></div>
        <div className="rounded-xl border border-slate-200 bg-white p-3"><div className="text-xs text-slate-500">منتهية</div><div className="text-xl font-bold text-slate-900">{done.length}</div></div>
        <div className="rounded-xl border border-slate-200 bg-white p-3"><div className="text-xs text-slate-500">انتصارات المنشأة</div><div className="text-xl font-bold text-teal-700" data-testid="matches-wins">{wins.length}</div></div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white">
        <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-4 py-3">
          <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }} className={selectCls} data-testid="match-filter-status">
            <option value="">كل الحالات</option>
            <option value="upcoming">قادمة</option>
            <option value="live">جارية</option>
            <option value="awaiting_result">بانتظار النتيجة</option>
            <option value="done">منتهية</option>
            <option value="cancelled">ملغية</option>
          </select>
          <select value={catFilter} onChange={(e) => { setCatFilter(e.target.value); setPage(1); }} className={selectCls} data-testid="match-filter-category">
            <option value="">كل الفئات</option>
            {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select value={tourFilter} onChange={(e) => { setTourFilter(e.target.value); setPage(1); }} className={selectCls} data-testid="match-filter-tournament">
            <option value="">كل البطولات</option>
            {tournaments.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          <span className="mr-auto text-xs text-slate-400">{filtered.length} مباراة</span>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 gap-4 p-4 sm:gap-5 md:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3, 4, 5, 6].map((i) => <div key={i} className="h-48 animate-pulse rounded-xl bg-slate-100" />)}
          </div>
        ) : paged.length === 0 ? <div className="p-6"><EmptyState icon="calendar" text="لا توجد مباريات مطابقة" testId="matches-empty" /></div>
          : (
            <div className="grid grid-cols-1 gap-4 p-4 sm:gap-5 md:grid-cols-2 lg:grid-cols-3">
              {paged.map((m) => (
                <div key={m.id} className="relative flex flex-col justify-between overflow-hidden rounded-xl border border-slate-200 bg-white p-4 shadow-sm transition-all duration-200 hover:border-teal-500/40 hover:shadow-md" data-testid="match-card">
                  <div className="mb-3 flex items-center justify-between gap-2 border-b border-slate-100 pb-3">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${STATUS_CLS[m.status] || 'bg-slate-100 text-slate-600'} ${m.status === 'live' ? 'animate-pulse' : ''}`} data-testid="match-status-badge">{STATUS[m.status] || m.status}</span>
                      {resultBadge(m)}
                    </div>
                    <div className="flex items-center gap-1">
                      <button onClick={() => setDetail(m)} className="rounded-lg border border-slate-200 px-2 py-1 text-[11px] font-semibold text-slate-600 hover:border-teal-300 hover:text-teal-700" data-testid="match-manage-btn">إدارة</button>
                      <button onClick={() => openForm(m)} className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100 hover:text-teal-700" data-testid="match-edit-btn"><Icon name="cog" className="w-4 h-4" /></button>
                      <button onClick={() => setConfirmDel(m)} className="rounded-lg p-1.5 text-slate-500 hover:bg-red-50 hover:text-red-600" data-testid="match-del-btn"><Icon name="trash" className="w-4 h-4" /></button>
                    </div>
                  </div>

                  <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2 py-1 text-center">
                    <div className="flex min-w-0 flex-col items-center gap-1.5">
                      <TeamLogo url={m.home_team_logo_url} fileId={m.home_team_logo_file_id} name={m.home_team_name} className="h-11 w-11" />
                      <span className="w-full truncate text-sm font-bold text-slate-800">{m.home_team_name}</span>
                    </div>
                    <div className="shrink-0 min-w-[72px]">
                      {isDone(m.status) && m.home_score != null
                        ? <div className="text-xl font-extrabold tracking-wider text-[#16233b]" dir="ltr" data-testid="match-score">{m.away_score} - {m.home_score}</div>
                        : <div className="text-xs font-medium text-slate-500">{fmtDT(m.match_date)}</div>}
                      <MatchCountdown match={m} />
                    </div>
                    <div className="flex min-w-0 flex-col items-center gap-1.5">
                      <TeamLogo url={m.away_team_logo_url} fileId={m.away_team_logo_file_id} name={m.away_team_name} className="h-11 w-11" />
                      <span className="w-full truncate text-sm font-bold text-slate-800">{m.away_team_name}</span>
                    </div>
                  </div>

                  <div className="mt-3 flex items-center justify-between gap-2 border-t border-slate-100 pt-3 text-xs text-slate-500">
                    <span className="truncate">{m.category_name || ''}{m.tournament_name ? ` • ${m.tournament_name}` : ''}</span>
                    {canFinance && m.total_cost > 0 && <span className="shrink-0 font-semibold text-slate-600" data-testid="match-total-cost">{money(m.total_cost)}</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        <Paginator page={page} total={filtered.length} limit={10} onChange={setPage} />
      </div>

      <Modal open={modal} title={editing ? 'تعديل المباراة' : 'إضافة مباراة'} onClose={() => setModal(false)} wide>
        {formError && <div className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="match-form-error">{formError}</div>}
        <form onSubmit={submit} className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="الفريق المضيف" required>
            <select className={inputCls} value={form.home_team_id || ''} onChange={(e) => setForm({ ...form, home_team_id: e.target.value })} data-testid="match-input-home">
              <option value="">اختر الفريق</option>
              {teams.map((t) => <option key={t.id} value={t.id}>{t.name}{t.is_club_team ? ' (فريق المنشأة)' : ''}</option>)}
            </select>
          </Field>
          <Field label="الفريق الضيف" required>
            <select className={inputCls} value={form.away_team_id || ''} onChange={(e) => setForm({ ...form, away_team_id: e.target.value })} data-testid="match-input-away">
              <option value="">اختر الفريق</option>
              {teams.map((t) => <option key={t.id} value={t.id}>{t.name}{t.is_club_team ? ' (فريق المنشأة)' : ''}</option>)}
            </select>
          </Field>
          <Field label="الفئة" required>
            <select className={inputCls} value={form.category_id || ''} onChange={(e) => setForm({ ...form, category_id: e.target.value })} data-testid="match-input-category">
              <option value="">اختر الفئة</option>
              {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </Field>
          <Field label="البطولة">
            <select className={inputCls} value={form.tournament_id || ''} onChange={(e) => setForm({ ...form, tournament_id: e.target.value })}>
              <option value="">بدون بطولة</option>
              {tournaments.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </Field>
          <Field label="الموسم">
            <select className={inputCls} value={form.season_id || ''} onChange={(e) => setForm({ ...form, season_id: e.target.value })} data-testid="match-input-season">
              <option value="">بدون موسم</option>
              {seasons.map((s) => <option key={s.id} value={s.id}>{s.name}{s.status === 'active' ? ' (نشط)' : ''}</option>)}
            </select>
          </Field>
          <Field label="تاريخ ووقت المباراة" required><input type="datetime-local" className={inputCls} value={form.match_date || ''} onChange={(e) => setForm({ ...form, match_date: e.target.value })} data-testid="match-input-date" /></Field>
          <Field label="الملعب"><input className={inputCls} value={form.venue || ''} onChange={(e) => setForm({ ...form, venue: e.target.value })} /></Field>
          <Field label="نوع المباراة">
            <select className={inputCls} value={form.match_type || 'external'} onChange={(e) => setForm({ ...form, match_type: e.target.value })}>
              <option value="external">خارجية</option>
              <option value="internal">داخلية</option>
            </select>
          </Field>
          <Field label="الحالة">
            <select className={inputCls} value={form.status || 'upcoming'} onChange={(e) => setForm({ ...form, status: e.target.value })}>
              {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </Field>
          <div className="col-span-full"><Field label="ملاحظات"><input className={inputCls} value={form.notes || ''} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field></div>
          <div className="col-span-full flex justify-end gap-2">
            <button type="button" onClick={() => setModal(false)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600">إلغاء</button>
            <PrimaryBtn type="submit" data-testid="match-form-submit">حفظ</PrimaryBtn>
          </div>
        </form>
      </Modal>

      {detail && <MatchDetail match={detail} user={user} onClose={() => setDetail(null)} onChanged={load} notify={notify} />}

      <Modal open={Boolean(importState)} title="استيراد المباريات من Excel" onClose={() => setImportState(null)} wide>
        {importState?.busy && !importState?.preview && <p className="text-sm text-slate-500">جارٍ قراءة الملف...</p>}
        {importState?.error && <div className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="matches-import-error">{importState.error}</div>}
        {importState?.preview && (
          <div className="space-y-3" data-testid="matches-import-preview">
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="rounded-lg bg-slate-50 p-2"><div className="text-lg font-bold text-slate-900">{importState.preview.total_matches || 0}</div><div className="text-xs text-slate-500">مباراة</div></div>
              <div className="rounded-lg bg-slate-50 p-2"><div className="text-lg font-bold text-teal-700">{importState.preview.new_teams_count || 0}</div><div className="text-xs text-slate-500">فريق جديد سيُنشأ</div></div>
              <div className="rounded-lg bg-slate-50 p-2"><div className="text-lg font-bold text-slate-900">{importState.preview.existing_teams_count || 0}</div><div className="text-xs text-slate-500">فريق موجود</div></div>
            </div>
            <div className="max-h-56 overflow-y-auto rounded-lg border border-slate-100">
              {(importState.preview.matches_preview || []).slice(0, 50).map((m, i) => (
                <div key={i} className="flex justify-between border-b border-slate-50 px-3 py-1.5 text-sm">
                  <span className="font-semibold text-slate-700">{m.home_team_name || m.home_team} × {m.away_team_name || m.away_team}</span>
                  <span className="text-xs text-slate-400">{m.match_date ? String(m.match_date).slice(0, 10) : ''}</span>
                </div>
              ))}
            </div>
            {Boolean(importState.preview.errors?.length) && (
              <div className="max-h-32 overflow-y-auto rounded-lg bg-amber-50 p-2 text-xs text-amber-700">{importState.preview.errors.slice(0, 20).map((e2, i) => <div key={i}>{typeof e2 === 'string' ? e2 : JSON.stringify(e2)}</div>)}</div>
            )}
            <div className="flex justify-end gap-2">
              <button onClick={() => setImportState(null)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600">إلغاء</button>
              <PrimaryBtn onClick={confirmImport} disabled={importState.busy || !(importState.preview.matches_preview?.length)} data-testid="matches-import-confirm">{importState.busy ? 'جارٍ الاستيراد...' : 'تأكيد الاستيراد'}</PrimaryBtn>
            </div>
          </div>
        )}
      </Modal>

      <Modal open={Boolean(confirmDel)} title="حذف المباراة" onClose={() => setConfirmDel(null)}>
        <p className="text-sm text-slate-600">سيتم حذف المباراة مع جميع أهدافها وبطاقاتها وتصحيح إحصائيات اللاعبين. متابعة؟</p>
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={() => setConfirmDel(null)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600">إلغاء</button>
          <button onClick={remove} className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white" data-testid="match-confirm-del">حذف</button>
        </div>
      </Modal>
    </div>
  );
}

function MatchDetail({ match, user, onClose, onChanged, notify }) {
  const canFinance = canViewFinance(user);
  const [tab, setTab] = useState('result');
  const [goals, setGoals] = useState([]);
  const [cards, setCards] = useState([]);
  const [tp, setTp] = useState(null);
  const [costs, setCosts] = useState([]);
  const [costsTotal, setCostsTotal] = useState(0);
  const [costForm, setCostForm] = useState({ title: '', category: 'other', amount: '', expense_date: new Date().toISOString().slice(0, 10), payment_method: 'cash', vendor_name: '', notes: '' });
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const receiptInputRef = useRef(null);
  const [uploadingFor, setUploadingFor] = useState(null);
  const [result, setResult] = useState({ home_score: match.home_score ?? '', away_score: match.away_score ?? '', status: isDone(match.status) ? match.status : 'completed' });
  const [goalForm, setGoalForm] = useState({ player_id: '', team_id: '', minute: '', is_penalty: false });
  const [cardForm, setCardForm] = useState({ player_id: '', team_id: '', card_type: 'yellow', minute: '', reason: '' });
  const [err, setErr] = useState(null);

  const loadCosts = useCallback(async () => {
    if (!canFinance) return;
    try {
      const res = await axios.get(`${API}/matches/${match.id}/costs?include_voided=true`, auth());
      setCosts(res.data?.items || []);
      setCostsTotal(res.data?.total || 0);
    } catch { setCosts([]); setCostsTotal(0); }
  }, [match.id, canFinance]);

  const loadEvents = useCallback(async () => {
    const g = (u) => axios.get(`${API}${u}`, auth()).then((r) => r.data).catch(() => null);
    const [gl, cd, tpd] = await Promise.all([g(`/matches/${match.id}/goals`), g(`/matches/${match.id}/cards`), g(`/matches/${match.id}/teams-players`)]);
    setGoals(Array.isArray(gl) ? gl : []);
    setCards(Array.isArray(cd) ? cd : []);
    setTp(tpd);
    loadCosts();
  }, [match.id, loadCosts]);
  useEffect(() => { loadEvents(); }, [loadEvents]);

  const players = tp?.available_players || [];
  const playerName = (id) => players.find((p) => p.id === id)?.name || 'لاعب';
  const clubTeamId = tp?.home_team?.is_club_team ? tp.home_team.id : tp?.away_team?.is_club_team ? tp.away_team.id : null;

  const addCost = async (e) => {
    e.preventDefault(); setErr(null);
    if (!costForm.title.trim() || !costForm.amount) { setErr('أدخل وصف التكلفة والمبلغ'); return; }
    try {
      await axios.post(`${API}/matches/${match.id}/costs`, { ...costForm, amount: Number(costForm.amount) }, auth());
      setCostForm({ title: '', category: 'other', amount: '', expense_date: new Date().toISOString().slice(0, 10), payment_method: 'cash', vendor_name: '', notes: '' });
      notify('تم إضافة بند التكلفة'); loadCosts(); onChanged();
    } catch (e2) { setErr(errMsg(e2, 'فشل إضافة التكلفة')); }
  };

  const cancelCost = async () => {
    try {
      await axios.delete(`${API}/expenses/${cancelTarget.id}`, { ...auth(), params: { reason: cancelReason || undefined } });
      notify('تم إلغاء بند التكلفة'); setCancelTarget(null); setCancelReason(''); loadCosts(); onChanged();
    } catch (e2) { notify(errMsg(e2, 'فشل الإلغاء')); }
  };

  const uploadReceipt = async (costId, file) => {
    if (!file) return;
    setUploadingFor(costId);
    const fd = new FormData(); fd.append('file', file);
    try {
      await axios.post(`${API}/matches/${match.id}/costs/${costId}/receipt`, fd, auth());
      notify('تم رفع الإيصال'); loadCosts();
    } catch (e2) { notify(errMsg(e2, 'فشل رفع الإيصال')); }
    setUploadingFor(null);
  };

  const viewReceipt = async (costId) => {
    try {
      const res = await axios.get(`${API}/matches/${match.id}/costs/${costId}/receipt`, { ...auth(), responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      window.open(url, '_blank');
    } catch { notify('تعذر تنزيل الإيصال'); }
  };

  const saveResult = async () => {
    setErr(null);
    if (result.home_score === '' || result.away_score === '') { setErr('أدخل نتيجة الفريقين'); return; }
    try {
      await axios.put(`${API}/matches/${match.id}/result`, { home_score: Number(result.home_score), away_score: Number(result.away_score), status: result.status, notes: match.notes || '' }, auth());
      notify('تم حفظ النتيجة'); onChanged();
    } catch (e2) { setErr(errMsg(e2, 'فشل حفظ النتيجة')); }
  };

  const addGoal = async (e) => {
    e.preventDefault(); setErr(null);
    if (!goalForm.player_id || goalForm.minute === '') { setErr('اختر اللاعب وأدخل الدقيقة'); return; }
    try {
      await axios.post(`${API}/goals`, { match_id: match.id, player_id: goalForm.player_id, team_id: goalForm.team_id || clubTeamId || match.home_team_id, minute: Number(goalForm.minute), is_penalty: goalForm.is_penalty, is_own_goal: false }, auth());
      setGoalForm({ player_id: '', team_id: '', minute: '', is_penalty: false }); notify('تم تسجيل الهدف'); loadEvents();
    } catch (e2) { setErr(errMsg(e2, 'فشل تسجيل الهدف')); }
  };

  const addCard = async (e) => {
    e.preventDefault(); setErr(null);
    if (!cardForm.player_id || cardForm.minute === '') { setErr('اختر اللاعب وأدخل الدقيقة'); return; }
    try {
      await axios.post(`${API}/cards`, { match_id: match.id, player_id: cardForm.player_id, team_id: cardForm.team_id || clubTeamId || match.home_team_id, card_type: cardForm.card_type, minute: Number(cardForm.minute), reason: cardForm.reason || null }, auth());
      setCardForm({ player_id: '', team_id: '', card_type: 'yellow', minute: '', reason: '' }); notify('تم تسجيل البطاقة'); loadEvents();
    } catch (e2) { setErr(errMsg(e2, 'فشل تسجيل البطاقة')); }
  };

  const delGoal = async (id) => { try { await axios.delete(`${API}/goals/${id}`, auth()); loadEvents(); } catch (e2) { setErr(errMsg(e2, 'فشل الحذف')); } };
  const delCard = async (id) => { try { await axios.delete(`${API}/cards/${id}`, auth()); loadEvents(); } catch (e2) { setErr(errMsg(e2, 'فشل الحذف')); } };

  const tabs = [['result', 'النتيجة'], ['goals', `الأهداف (${goals.length})`], ['cards', `البطاقات (${cards.length})`]];
  if (canFinance) tabs.push(['costs', `التكلفة${costsTotal ? ` (${money(costsTotal)})` : ''}`]);

  return (
    <Modal open title={`إدارة المباراة: ${match.home_team_name} × ${match.away_team_name}`} onClose={onClose} wide>
      <div className="mb-3 flex gap-1 rounded-lg bg-slate-100 p-1" data-testid="match-detail-tabs">
        {tabs.map(([k, v]) => (
          <button key={k} onClick={() => { setTab(k); setErr(null); }} className={`flex-1 rounded-md px-3 py-1.5 text-sm font-semibold ${tab === k ? 'bg-white text-[#16233b] shadow-sm' : 'text-slate-500'}`} data-testid={`match-tab-${k}`}>{v}</button>
        ))}
      </div>
      {err && <div className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="match-detail-error">{err}</div>}

      {tab === 'result' && (
        <div className="space-y-3" data-testid="match-result-form">
          <div className="flex items-center justify-center gap-3">
            <div className="text-center">
              <div className="mb-1 text-xs font-semibold text-slate-600">{match.home_team_name}</div>
              <input type="number" min="0" className="w-20 rounded-lg border border-slate-200 px-2 py-2 text-center text-lg font-bold" value={result.home_score} onChange={(e) => setResult({ ...result, home_score: e.target.value })} data-testid="result-home-input" />
            </div>
            <span className="text-slate-400">—</span>
            <div className="text-center">
              <div className="mb-1 text-xs font-semibold text-slate-600">{match.away_team_name}</div>
              <input type="number" min="0" className="w-20 rounded-lg border border-slate-200 px-2 py-2 text-center text-lg font-bold" value={result.away_score} onChange={(e) => setResult({ ...result, away_score: e.target.value })} data-testid="result-away-input" />
            </div>
          </div>
          <Field label="حالة المباراة">
            <select className={inputCls} value={result.status} onChange={(e) => setResult({ ...result, status: e.target.value })}>
              <option value="completed">مكتملة</option>
              <option value="live">جارية</option>
              <option value="cancelled">ملغية</option>
            </select>
          </Field>
          <div className="flex justify-end"><PrimaryBtn onClick={saveResult} data-testid="result-save-btn">حفظ النتيجة</PrimaryBtn></div>
        </div>
      )}

      {tab === 'goals' && (
        <div className="space-y-3">
          <div className="max-h-40 space-y-1 overflow-y-auto">
            {goals.length === 0 ? <p className="py-3 text-center text-sm text-slate-400">لا توجد أهداف مسجلة</p> : goals.map((g) => (
              <div key={g.id} className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-1.5 text-sm" data-testid="goal-row">
                <span className="font-semibold text-slate-700">⏱ {g.minute}' — {playerName(g.player_id)}{g.is_penalty ? ' (ركلة جزاء)' : ''}</span>
                <button onClick={() => delGoal(g.id)} className="rounded p-1 text-slate-400 hover:text-red-600" data-testid="goal-del-btn"><Icon name="trash" className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
          <form onSubmit={addGoal} className="grid grid-cols-2 gap-2 rounded-lg border border-slate-100 p-3">
            <Field label="اللاعب" required>
              <select className={inputCls} value={goalForm.player_id} onChange={(e) => setGoalForm({ ...goalForm, player_id: e.target.value })} data-testid="goal-player-select">
                <option value="">اختر اللاعب</option>
                {players.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </Field>
            <Field label="الدقيقة" required><input type="number" min="0" max="130" className={inputCls} value={goalForm.minute} onChange={(e) => setGoalForm({ ...goalForm, minute: e.target.value })} data-testid="goal-minute-input" /></Field>
            <label className="col-span-full flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" checked={goalForm.is_penalty} onChange={(e) => setGoalForm({ ...goalForm, is_penalty: e.target.checked })} /> ركلة جزاء</label>
            <div className="col-span-full flex justify-end"><PrimaryBtn type="submit" data-testid="goal-add-btn">تسجيل هدف</PrimaryBtn></div>
          </form>
        </div>
      )}

      {tab === 'cards' && (
        <div className="space-y-3">
          <div className="max-h-40 space-y-1 overflow-y-auto">
            {cards.length === 0 ? <p className="py-3 text-center text-sm text-slate-400">لا توجد بطاقات مسجلة</p> : cards.map((c) => (
              <div key={c.id} className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-1.5 text-sm" data-testid="card-row">
                <span className="font-semibold text-slate-700"><span className={`ml-1 inline-block h-3 w-2.5 rounded-sm align-middle ${c.card_type === 'red' ? 'bg-red-500' : 'bg-amber-400'}`} /> {c.minute}' — {playerName(c.player_id)}{c.reason ? ` (${c.reason})` : ''}</span>
                <button onClick={() => delCard(c.id)} className="rounded p-1 text-slate-400 hover:text-red-600" data-testid="card-del-btn"><Icon name="trash" className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
          <form onSubmit={addCard} className="grid grid-cols-2 gap-2 rounded-lg border border-slate-100 p-3">
            <Field label="اللاعب" required>
              <select className={inputCls} value={cardForm.player_id} onChange={(e) => setCardForm({ ...cardForm, player_id: e.target.value })} data-testid="card-player-select">
                <option value="">اختر اللاعب</option>
                {players.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </Field>
            <Field label="الدقيقة" required><input type="number" min="0" max="130" className={inputCls} value={cardForm.minute} onChange={(e) => setCardForm({ ...cardForm, minute: e.target.value })} data-testid="card-minute-input" /></Field>
            <Field label="نوع البطاقة">
              <select className={inputCls} value={cardForm.card_type} onChange={(e) => setCardForm({ ...cardForm, card_type: e.target.value })}>
                <option value="yellow">صفراء</option>
                <option value="red">حمراء</option>
              </select>
            </Field>
            <Field label="السبب"><input className={inputCls} value={cardForm.reason} onChange={(e) => setCardForm({ ...cardForm, reason: e.target.value })} /></Field>
            <div className="col-span-full flex justify-end"><PrimaryBtn type="submit" data-testid="card-add-btn">تسجيل بطاقة</PrimaryBtn></div>
          </form>
        </div>
      )}

      {tab === 'costs' && canFinance && (
        <div className="space-y-3" data-testid="match-costs-tab">
          <div className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2">
            <span className="text-sm font-semibold text-slate-600">إجمالي تكلفة المباراة (غير الملغى)</span>
            <span className="text-base font-bold text-[#16233b]" data-testid="match-cost-total">{money(costsTotal)}</span>
          </div>

          <div className="max-h-56 space-y-1 overflow-y-auto">
            {costs.length === 0 ? <p className="py-3 text-center text-sm text-slate-400">لا توجد بنود تكلفة مسجلة</p> : costs.map((c) => (
              <div key={c.id} className={`rounded-lg border border-slate-100 px-3 py-2 ${c.status === 'voided' ? 'bg-slate-50 opacity-60' : 'bg-slate-50/60'}`} data-testid="match-cost-row">
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className={`truncate text-sm font-semibold text-slate-800 ${c.status === 'voided' ? 'line-through' : ''}`}>{c.title} <span className="text-xs text-slate-400">({EXPENSE_CATS[c.category] || c.category})</span></div>
                    <div className="text-xs text-slate-500">{fmtDate(c.expense_date)}{c.vendor_name ? ` • ${c.vendor_name}` : ''} • {PAY_METHODS[c.payment_method] || c.payment_method}</div>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <StatusBadge status={c.status} />
                    <span className={`text-sm font-bold text-slate-900 ${c.status === 'voided' ? 'line-through' : ''}`}>{money(c.amount)}</span>
                  </div>
                </div>
                <div className="mt-2 flex items-center gap-2">
                  {c.receipt_storage_path
                    ? <button onClick={() => viewReceipt(c.id)} className="flex items-center gap-1 rounded-lg border border-slate-200 px-2 py-1 text-xs text-slate-600 hover:border-teal-300" data-testid="match-cost-receipt-view"><Icon name="download" className="w-3.5 h-3.5" /> عرض الإيصال</button>
                    : c.status !== 'voided' && (
                      <>
                        <button onClick={() => { receiptInputRef.current.dataset.costId = c.id; receiptInputRef.current.click(); }} disabled={uploadingFor === c.id} className="flex items-center gap-1 rounded-lg border border-slate-200 px-2 py-1 text-xs text-slate-600 hover:border-teal-300 disabled:opacity-50" data-testid="match-cost-receipt-upload">
                          <Icon name="document" className="w-3.5 h-3.5" /> {uploadingFor === c.id ? 'جارٍ الرفع...' : 'رفع إيصال'}
                        </button>
                      </>
                    )}
                  {c.status !== 'voided' && (
                    <button onClick={() => setCancelTarget(c)} className="flex items-center gap-1 rounded-lg px-2 py-1 text-xs text-red-600 hover:bg-red-50" data-testid="match-cost-cancel-btn">
                      <Icon name="trash" className="w-3.5 h-3.5" /> إلغاء
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          <input ref={receiptInputRef} type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={(e) => { const id = receiptInputRef.current.dataset.costId; const f = e.target.files?.[0]; e.target.value = ''; if (id && f) uploadReceipt(id, f); }} data-testid="match-cost-receipt-input" />

          <form onSubmit={addCost} className="grid grid-cols-2 gap-2 rounded-lg border border-slate-100 p-3">
            <div className="col-span-full"><Field label="وصف التكلفة" required><input className={inputCls} value={costForm.title} onChange={(e) => setCostForm({ ...costForm, title: e.target.value })} data-testid="match-cost-title-input" /></Field></div>
            <Field label="التصنيف" required>
              <select className={inputCls} value={costForm.category} onChange={(e) => setCostForm({ ...costForm, category: e.target.value })} data-testid="match-cost-category-select">
                {Object.entries(EXPENSE_CATS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </Field>
            <Field label="المبلغ" required><input type="number" min="0.01" step="0.01" className={inputCls} value={costForm.amount} onChange={(e) => setCostForm({ ...costForm, amount: e.target.value })} data-testid="match-cost-amount-input" /></Field>
            <Field label="التاريخ" required><input type="date" className={inputCls} value={costForm.expense_date} onChange={(e) => setCostForm({ ...costForm, expense_date: e.target.value })} data-testid="match-cost-date-input" /></Field>
            <Field label="طريقة الدفع">
              <select className={inputCls} value={costForm.payment_method} onChange={(e) => setCostForm({ ...costForm, payment_method: e.target.value })} data-testid="match-cost-payment-select">
                {Object.entries(PAY_METHODS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </Field>
            <Field label="المورّد/المستفيد"><input className={inputCls} value={costForm.vendor_name} onChange={(e) => setCostForm({ ...costForm, vendor_name: e.target.value })} data-testid="match-cost-vendor-input" /></Field>
            <div className="col-span-full"><Field label="ملاحظات"><input className={inputCls} value={costForm.notes} onChange={(e) => setCostForm({ ...costForm, notes: e.target.value })} data-testid="match-cost-notes-input" /></Field></div>
            <div className="col-span-full flex justify-end"><PrimaryBtn type="submit" data-testid="match-cost-add-btn">إضافة بند تكلفة</PrimaryBtn></div>
          </form>
        </div>
      )}

      <Modal open={Boolean(cancelTarget)} title="إلغاء بند التكلفة" onClose={() => { setCancelTarget(null); setCancelReason(''); }}>
        <p className="mb-3 text-sm text-slate-600">سيبقى البند مسجلاً للتدقيق بحالة «ملغى» ويُستبعد من إجمالي التكلفة.</p>
        <Field label="سبب الإلغاء (اختياري)"><input className={inputCls} value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} data-testid="match-cost-cancel-reason-input" /></Field>
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={() => { setCancelTarget(null); setCancelReason(''); }} className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600">تراجع</button>
          <button onClick={cancelCost} className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white" data-testid="match-cost-cancel-confirm-btn">تأكيد الإلغاء</button>
        </div>
      </Modal>
    </Modal>
  );
}
